import { useState, useMemo, useEffect, useCallback } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import clsx from 'clsx';

import { useForm, useFormRefs, useFieldProps, useTranslateAndReplace } from '../hooks';
import { FormAutocompleteProps } from '../types';
import { throttleFetch } from '../functions';

import FormInput, { useFormInputStyles } from './FormInput';
import FormHelperText from './FormHelperText';
import { get as _get } from 'lodash';
import { TranslateAndReplaceParams } from '../context';


const fetch = throttleFetch(200);

function FormAutocomplete<T>({
	description,
	isLoading,
	defaultValue,
	initialValue,
	id,
	fetchFunction,
	setDefaultValue,
	onChangeOptionsFilter,
	valueForForm = (value) => value,
	classes: { container, labelContainer, label, errorContainer, ...inputClasses } = {},
	...rest
}: FormAutocompleteProps<T>) {
	const [valueNoDefault, setValueNoDefault] = useState<T | null>(initialValue || null);
	const [selectOptions, setSelectOptions] = useState<Array<T>>([]);
	const [selectedOption, setSelectedOption] = useState<T | null>(initialValue && valueForForm(initialValue) || null);
	const [inputValue, setInputValue] = useState('');

	const formInputClasses = useFormInputStyles();
	const form = useForm();
	const innerRef = useFormRefs();
	const inputProps = useFieldProps(id);

	const value = useMemo(() => defaultValue ? defaultValue : valueNoDefault, [defaultValue, valueNoDefault]);
	const setValue = useMemo(() => setDefaultValue ? setDefaultValue : setValueNoDefault, [setDefaultValue, setValueNoDefault]);
	const translateAndReplace = useTranslateAndReplace();
	const error = useMemo(() => {
		const isFieldTouched = _get(form.touched, id);
		const fieldValue = _get(form.values, id);
		return (
			(isFieldTouched && (!fieldValue || (isFieldTouched && selectedOption !== fieldValue)))
				?
				(translateAndReplace(_get(form.errors, id) as TranslateAndReplaceParams))
				: null
		);
	}, [form, selectedOption, id]);

	const onFocusFetch = useCallback(
		async () => {
			const newOptions = await fetchFunction(inputValue);
			if (selectOptions.length) return;
			setSelectOptions(newOptions);
		}, [fetchFunction, selectOptions, inputValue]);

	const onChange = useCallback(
		(_event: React.ChangeEvent<{}>, newValue: T | null) => {
			if (!newValue) return
			setValue(newValue);
			onChangeOptionsFilter ? setSelectOptions(options => onChangeOptionsFilter(newValue, options)) : setSelectOptions(options => [newValue, ...options]);
			setSelectedOption(valueForForm(newValue));
			form.setFieldValue(id, valueForForm(newValue));
		}, [setValue, setSelectOptions, setSelectedOption, valueForForm, form, id]);

	const onBlur = useCallback(
		() => {
			setSelectOptions([]);
			setInputValue('');
			form.setFieldValue(id, value ? valueForForm(value) : "");
		}, [value, setSelectOptions, setInputValue, valueForForm, form, id]);

	const onInputChange = useCallback(
		(event, newInputValue) => {
			if (!event) return;
			if (event.type === "click" || event.type === "onKeyDown")
				return (setInputValue(''), setValue(null), form.setFieldValue(id, ''));
			if (event.type === 'blur')
				return setInputValue('');
			setInputValue(newInputValue);
		}, [setInputValue, setValue, form, id]);

	useEffect(() => {
		let active = true;
		if (isLoading || !inputValue) return;
		fetch(inputValue, async (inputValue: string) => {
			const newOptions = await fetchFunction(inputValue);
			if (!active) return;
			setSelectOptions(newOptions);
		});
		return () => {
			active = false;
		};
	}, [inputValue, value, isLoading, fetchFunction]);

	if (isLoading) return <FormInput id={id} description={description} loading={true} />

	return (
		<Autocomplete
			autoComplete
			value={value}
			handleHomeEndKeys
			id={id}
			includeInputInList
			options={selectOptions}
			filterOptions={(x) => x}
			innerRef={() => innerRef}
			onChange={onChange}
			onInputChange={onInputChange}
			renderInput={(params) => (
				<div
					onBlur={inputProps.onBlur}
					ref={params.InputProps.ref}
					className={container}
				>
					<div className={clsx(formInputClasses.containLables, labelContainer)}>
						<InputLabel
							{...params.InputLabelProps}
							className={clsx(formInputClasses.label, label)}>
							{description}
						</InputLabel>
					</div>
					<div
						onFocus={onFocusFetch}
						onBlur={onBlur}
					>
						<Input
							type="text"
							autoComplete="off"
							disableUnderline
							inputRef={(el) => { if (el && innerRef) innerRef.current[id!] = el }}
							inputProps={{ ...params.inputProps, form: { autocomplete: rest.autoComplete === false ? 'off' : undefined } }}
							classes={inputClasses}
							{...params.InputProps}
							{...inputProps}
							className={clsx(formInputClasses.input, error && formInputClasses.border)}
						/>
					</div>
					<FormHelperText className={errorContainer} text={error} error />
				</div>
			)}
			{...rest}
		/>
	);
};

export default FormAutocomplete;